import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { ArrowLeft, CheckCircle2, KeyRound } from 'lucide-react';
import BrandLogo from '../../components/common/BrandLogo';
import { toast } from 'sonner';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/password-reset/confirm', { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Zurücksetzen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <BrandLogo height={40} />
        </div>

        {!done ? (
          <>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0F4C81] mb-6">
              <ArrowLeft className="w-4 h-4" /> Zurück zum Login
            </Link>
            <h1 className="font-heading text-3xl font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <KeyRound className="w-7 h-7 text-[#0F4C81]" /> Neues Passwort
            </h1>
            <p className="text-slate-600 text-sm mb-6">Wählen Sie ein neues Passwort für Ihr Konto.</p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Neues Passwort *</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
                  placeholder="Mindestens 6 Zeichen"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Passwort bestätigen *</label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-input"
                  className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !password || !confirm}
                data-testid="reset-submit-btn"
                className="w-full bg-[#001d4c] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2.5 text-sm disabled:opacity-50"
              >
                {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
              </button>
            </form>
          </>
        ) : (
          <div className="bg-emerald-50 border border-emerald-200 rounded-md p-5" data-testid="reset-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <h2 className="font-heading text-base font-semibold text-emerald-900 mb-2">Passwort geändert</h2>
                <p className="text-sm text-emerald-800">
                  Ihr Passwort wurde erfolgreich zurückgesetzt. Sie werden gleich zum Login weitergeleitet.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
